import { useState } from 'react';
import { Box, Btn, Field, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { ApiError } from '../api/client';

/**
 * Sign-in screen.
 *
 * Submits to the API, which sets an httpOnly session cookie — no token is ever
 * held in JavaScript or localStorage. The form is a real `<form>` so Enter
 * submits and password managers behave normally.
 */
export interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<void>;
  warehouseHint?: string;
}

export function LoginPage({ onLogin }: LoginPageProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (!email.trim() || !password) {
      setError('Enter both email and password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box className="min-h-screen grid place-items-center bg-surface-page px-4 py-10">
      <Box className="w-full max-w-[400px]">
        {/* Brand */}
        <Box className="flex flex-col items-center text-center">
          <Box
            className="grid place-items-center w-12 h-12 rounded-2xl ring-1 ring-hairline bg-surface-card"
            style={{ color: 'var(--life-transit)' }}
          >
            <Icon name="warehouse" className="w-6 h-6" strokeWidth={1.9} />
          </Box>
          <Txt as="h1" className="mt-4 text-[22px] font-semibold text-ink-primary tracking-[-0.01em]">
            Warehouse Orders
          </Txt>
          <Txt as="p" className="mt-1 text-[13px] text-ink-muted">
            Sign in to view order monitoring &amp; pickup tracking
          </Txt>
        </Box>

        {/* Card */}
        <Box className="mt-6 rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-6">
          <form
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Box className="flex flex-col gap-4">
              <Box>
                <Txt as="label" htmlFor="login-email" className="block text-[12px] font-medium text-ink-secondary">
                  Email
                </Txt>
                <Field
                  id="login-email"
                  type="text"
                  value={email}
                  onChangeText={(v) => {
                    setEmail(v);
                    setError(null);
                  }}
                  placeholder="you@company.com"
                  autoComplete="username"
                  aria-label="Email"
                  className="mt-1.5 w-full h-10 px-3 rounded-lg bg-surface-sunken text-ink-primary text-[14px] ring-1 ring-hairline placeholder:text-ink-muted focus:bg-surface-card transition-colors duration-150"
                />
              </Box>

              <Box>
                <Txt as="label" htmlFor="login-password" className="block text-[12px] font-medium text-ink-secondary">
                  Password
                </Txt>
                <Box className="relative mt-1.5 flex items-center">
                  <Field
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChangeText={(v) => {
                      setPassword(v);
                      setError(null);
                    }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    aria-label="Password"
                    className="w-full h-10 pl-3 pr-10 rounded-lg bg-surface-sunken text-ink-primary text-[14px] ring-1 ring-hairline placeholder:text-ink-muted focus:bg-surface-card transition-colors duration-150"
                  />
                  <Btn
                    onPress={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-1 grid place-items-center w-8 h-8 rounded-md text-ink-muted hover:text-ink-primary transition-colors duration-150"
                  >
                    <Icon name={showPassword ? 'eye-off' : 'eye'} className="w-4 h-4" />
                  </Btn>
                </Box>
              </Box>

              {error ? (
                <Box
                  role="alert"
                  className="flex items-start gap-2 rounded-lg px-3 py-2.5 ring-1 ring-hairline"
                  style={{ backgroundColor: 'var(--surface-sunken)' }}
                >
                  <Box className="shrink-0 mt-px" style={{ color: 'var(--life-returned)' }}>
                    <Icon name="alert" className="w-4 h-4" strokeWidth={2} />
                  </Box>
                  <Txt className="text-[12px] text-ink-primary">{error}</Txt>
                </Box>
              ) : null}

              <Btn
                type="submit"
                disabled={busy}
                className="w-full h-10 rounded-lg text-[14px] font-semibold bg-ink-primary text-surface-card hover:opacity-90 transition-opacity duration-150 disabled:opacity-60 disabled:pointer-events-none"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </Btn>
            </Box>
          </form>
        </Box>

        <Txt as="p" className="mt-5 text-center text-[11px] text-ink-muted">
          Shipanel database is accessed read-only. This dashboard never writes to it.
        </Txt>
      </Box>
    </Box>
  );
}
